import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Button, Badge, Icon } from '../../components/UI';
import clientService from '../../services/clientApi';
import ScheduleApiService from '../../services/scheduleApi';
import { Appointment } from '../../components/Calendar/types';
import { useAuth } from '../../auth/AuthContext';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color: 'blue' | 'green' | 'purple' | 'orange';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, trend, color }) => {
  const colorClasses = {
    blue: 'from-blue-400 to-blue-600',
    green: 'from-green-400 to-green-600', 
    purple: 'from-purple-400 to-purple-600',
    orange: 'from-orange-400 to-orange-600'
  };

  return (
    <Card className="relative overflow-hidden" hover>
      <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${colorClasses[color]} rounded-full -mr-12 -mt-12 opacity-10`}></div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {trend && (
            <div className={`flex items-center mt-2 text-xs ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
              <svg className={`w-3 h-3 mr-1 ${trend.isPositive ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              {trend.isPositive ? '+' : '-'}{Math.abs(trend.value)}%
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-gradient-to-br ${colorClasses[color]} text-white`}>
          <Icon name={icon} size={20} />
        </div>
      </div>
    </Card>
  );
};

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeClients, setActiveClients] = useState(0);
  const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
  const [weekAppointments, setWeekAppointments] = useState<Appointment[]>([]);

  const loadDashboardData = useCallback(async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const [clientsResponse, daySchedule, weekSchedule] = await Promise.all([
        clientService.getClients({ active: true, limit: 1, page: 1 }),
        ScheduleApiService.getDaySchedule(new Date()),
        ScheduleApiService.getWeekSchedule(new Date()),
      ]);

      const totalActiveClients = clientsResponse.meta?.total ?? clientsResponse.data.length;

      setActiveClients(totalActiveClients);
      setTodayAppointments(daySchedule.appointments || []);
      setWeekAppointments(weekSchedule.appointments || []);
    } catch (loadError) {
      console.error('Erreur chargement dashboard:', loadError);
      setError('Impossible de charger les donnees du dashboard.');
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadDashboardData(true);

    const refreshInterval = window.setInterval(() => {
      void loadDashboardData(false);
    }, 60000);

    return () => {
      window.clearInterval(refreshInterval);
    };
  }, [loadDashboardData]);

  const totalPlannedMinutesToday = useMemo(
    () => todayAppointments.reduce((total, appointment) => total + (appointment.duration || 0), 0),
    [todayAppointments]
  );

  const formattedPlannedTimeToday = useMemo(() => {
    const hours = Math.floor(totalPlannedMinutesToday / 60);
    const minutes = totalPlannedMinutesToday % 60;
    if (hours === 0) {
      return `${minutes} min`;
    }
    return `${hours}h ${String(minutes).padStart(2, '0')}min`;
  }, [totalPlannedMinutesToday]);

  const upcomingAppointments = useMemo(() => {
    const now = new Date();
    return [...todayAppointments]
      .filter((appointment) => new Date(appointment.startTime) >= now)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      .slice(0, 5);
  }, [todayAppointments]);

  const recentActivities = useMemo(() => {
    const completedToday = todayAppointments.filter((a) => a.status === 'completed').length;
    const upcomingCount = upcomingAppointments.length;

    return [
      {
        id: 1,
        message: `${todayAppointments.length} rendez-vous aujourd'hui`,
        time: 'Mise a jour en direct',
        icon: 'calendar',
        color: 'blue',
      },
      {
        id: 2,
        message: `${completedToday} rendez-vous termines aujourd'hui`,
        time: 'Mise a jour en direct',
        icon: 'users',
        color: 'green',
      },
      {
        id: 3,
        message: `${upcomingCount} rendez-vous restants aujourd'hui`,
        time: 'Mise a jour en direct',
        icon: 'lightbulb',
        color: 'purple',
      },
      {
        id: 4,
        message: `${activeClients} clients actifs`,
        time: 'Mise a jour en direct',
        icon: 'map',
        color: 'orange',
      },
    ];
  }, [todayAppointments, upcomingAppointments.length, activeClients]);

  const formatAppointmentTime = (dateValue: Date): string => {
    return new Date(dateValue).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrentDate = (): string => {
    return new Date().toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getStatusLabel = (status: Appointment['status']): string => {
    if (status === 'confirmed') return 'Confirme';
    if (status === 'scheduled') return 'Planifie';
    if (status === 'in_progress') return 'En cours';
    if (status === 'completed') return 'Termine';
    if (status === 'cancelled') return 'Annule';
    return 'Non presente';
  };

  const getStatusVariant = (status: Appointment['status']): 'success' | 'warning' | 'danger' | 'default' => {
    if (status === 'confirmed' || status === 'completed') return 'success';
    if (status === 'scheduled' || status === 'in_progress') return 'warning';
    if (status === 'cancelled' || status === 'no_show') return 'danger';
    return 'default';
  };

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="relative">
          <h1 className="text-2xl font-bold mb-2">Bonjour {user?.displayName || 'Utilisateur'} ! 👋</h1>
          <p className="text-blue-100 mb-4 text-sm">Voici un résumé de votre activité aujourd'hui</p>
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => window.location.href = '/schedule'}
              className="px-4 py-2 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors flex items-center"
            >
              <Icon name="calendar" size={16} className="mr-2" />
              Voir l'agenda
            </button>
            <button 
              onClick={() => window.location.href = '/schedule'}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-400 transition-colors flex items-center"
            >
              <Icon name="plus" size={16} className="mr-2" />
              Nouveau RDV
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Clients actifs"
          value={isLoading ? '...' : activeClients}
          icon="users"
          color="blue"
        />
        <StatCard
          title="RDV aujourd'hui"
          value={isLoading ? '...' : todayAppointments.length}
          icon="calendar"
          color="green"
        />
        <StatCard
          title="RDV cette semaine"
          value={isLoading ? '...' : weekAppointments.length}
          icon="map"
          color="purple"
        />
        <StatCard
          title="Temps planifie aujourd'hui"
          value={isLoading ? '...' : formattedPlannedTimeToday}
          icon="lightbulb"
          color="orange"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upcoming Appointments */}
        <div className="lg:col-span-2">
          <Card
            title="Prochains rendez-vous"
            subtitle={`Aujourd'hui, ${formatCurrentDate()}`}
            action={
              <Button size="sm" variant="ghost">
                <Icon name="calendar" className="mr-2" />
                Voir tout
              </Button>
            }
          >
            <div className="space-y-4">
              {!isLoading && upcomingAppointments.length === 0 && (
                <div className="p-4 bg-gray-50 rounded-xl text-sm text-gray-600">
                  Aucun rendez-vous a venir pour aujourd'hui.
                </div>
              )}

              {upcomingAppointments.map((appointment) => (
                <div key={appointment.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {formatAppointmentTime(appointment.startTime)}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {appointment.client
                          ? `${appointment.client.firstName} ${appointment.client.lastName}`
                          : 'Client inconnu'}
                      </h4>
                      <p className="text-sm text-gray-500">{appointment.client?.address || 'Adresse non renseignee'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant={getStatusVariant(appointment.status)}>
                      {getStatusLabel(appointment.status)}
                    </Badge>
                    <Button size="sm" variant="ghost">
                      <Icon name="phone" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Recent Activities */}
        <div>
          <Card title="Activité récente" padding="sm">
            <div className="space-y-3">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className={`p-2 rounded-lg ${
                    activity.color === 'green' ? 'bg-green-100 text-green-600' :
                    activity.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                    activity.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                    'bg-orange-100 text-orange-600'
                  }`}>
                    <Icon name={activity.icon} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {activity.message}
                    </p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Quick Actions */}
      <Card title="Actions rapides">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button 
            onClick={() => window.location.href = '/clients'}
            className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-sm transition-all border border-gray-200"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
              <Icon name="plus" size={18} className="text-blue-600" />
            </div>
            <p className="text-xs font-medium text-gray-900">Ajouter client</p>
          </button>
          
          <button 
            onClick={() => window.location.href = '/schedule'}
            className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-sm transition-all border border-gray-200"
          >
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-2">
              <Icon name="calendar" size={18} className="text-green-600" />
            </div>
            <p className="text-xs font-medium text-gray-900">Planifier RDV</p>
          </button>

          <button 
            onClick={() => window.location.href = '/suggestions'}
            className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-sm transition-all border border-gray-200"
          >
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mb-2">
              <Icon name="lightbulb" size={18} className="text-purple-600" />
            </div>
            <p className="text-xs font-medium text-gray-900">Optimiser</p>
          </button>

          <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-sm transition-all border border-gray-200">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mb-2">
              <Icon name="map" size={18} className="text-orange-600" />
            </div>
            <p className="text-xs font-medium text-gray-900">Voir la carte</p>
          </button>
        </div>
      </Card>
    </div>
  );
};